import { useCallback, useEffect, useMemo, useState } from 'react';
import { CHART_INTERVALS } from '../config/timeframes';
import { getCandleTiming } from '../lib/candleTime';
import { studyCurrentCandle, type CandleStudy } from '../lib/candleStudy';
import { assessHistoricalBreakthrough } from '../lib/historicalBreakthrough';
import { relativeStrengthIndex } from '../lib/indicators';
import { percentChange } from '../lib/number';
import { detectStructuralZones } from '../lib/supportResistance';
import { assessTimeframe } from '../lib/timeframeAnalysis';
import { getBinanceServerOffset } from '../services/exchanges/binance';
import { getVerifiedCandleHistory } from '../services/candleHistory';
import {
  subscribeBinanceKline,
  type KlineStreamState,
} from '../services/exchanges/binanceKlineStream';
import { subscribeBybitKline } from '../services/exchanges/bybitKlineStream';
import { subscribeOkxKline } from '../services/exchanges/okxKlineStream';
import { getCoinDetail } from '../services/marketDetails';
import { getSelectedInterval, rememberSelectedInterval } from '../services/coinAnalysisStore';
import type {
  BreakthroughAssessment,
  Candle,
  ChartInterval,
  CoinDetailSnapshot,
  DexSnapshot,
  StructuralZone,
  TimeframeAssessment,
} from '../types/market';

export interface CoinAnalysisState {
  loading: boolean;
  sourceMode: 'live' | 'mixed' | 'unavailable';
  streamStatus: KlineStreamState | 'idle';
  streamMessage: string;
  detail: CoinDetailSnapshot;
  selectedInterval: ChartInterval;
  setSelectedInterval: (interval: ChartInterval) => void;
  candles: Candle[];
  assessments: TimeframeAssessment[];
  zones: StructuralZone[];
  breakthrough: BreakthroughAssessment;
  currentRsi: number;
  candleStudy: CandleStudy;
  candleTiming: ReturnType<typeof getCandleTiming>;
  narrative: string[];
  error?: string;
}

const EMPTY_DEX: DexSnapshot = {
  pairCount: 0,
  identityConfidence: 'unavailable',
  source: 'Loading',
};

const CORE_INTERVALS: ChartInterval[] = ['15m', '1h', '4h', '1d', '1w'];
const BACKGROUND_INTERVALS: ChartInterval[] = ['5m', '30m', '2h'];

type MarketResolution = { exchange: 'binance' | 'bybit' | 'okx'; marketKind: 'spot' | 'futures' };
type CandleLoad = { candles: Candle[]; live: boolean; resolution?: MarketResolution };
type IntervalLoad = CandleLoad & { interval: ChartInterval };

function emptyDetail(symbol: string): CoinDetailSnapshot {
  const baseAsset = symbol.endsWith('USDT') ? symbol.slice(0, -4) : symbol;
  return {
    symbol,
    baseAsset,
    price: 0,
    change24h: 0,
    fundamentals: { symbol: baseAsset, source: 'Loading', confidence: 'unavailable', updatedAt: Date.now() },
    dex: EMPTY_DEX,
    derivatives: { fundingBehavior: 'unavailable', source: 'Loading' },
    attribution: [],
    attributionWindow: '24h-turnover',
    attributionConfidence: 'low',
    observedDriver: 'Analyzing market participation',
    observedDriverExplanation: 'Data is being collected from public market sources.',
    updatedAt: Date.now(),
  };
}

async function fetchCandlesWithFallback(
  symbol: string,
  interval: ChartInterval,
  serverNow: number,
): Promise<CandleLoad> {
  const limit = interval === '1d' || interval === '1w' ? 220 : 360;
  try {
    const result = await getVerifiedCandleHistory(symbol, interval, limit, 'spot', serverNow);
    return { candles: result.candles, live: true, resolution: { exchange: result.exchange, marketKind: result.marketKind } };
  } catch {
    return { candles: [], live: false };
  }
}

async function loadIntervals(
  symbol: string,
  intervals: ChartInterval[],
  serverNow: number,
): Promise<IntervalLoad[]> {
  const results = new Array<IntervalLoad>(intervals.length);
  let cursor = 0;
  async function runner(): Promise<void> {
    while (cursor < intervals.length) {
      const index = cursor;
      cursor += 1;
      const interval = intervals[index]!;
      try {
        results[index] = { interval, ...(await fetchCandlesWithFallback(symbol, interval, serverNow)) };
      } catch {
        results[index] = { interval, candles: [], live: false };
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(2, intervals.length) }, () => runner()));
  return results;
}

function intervalMaps(results: IntervalLoad[]): {
  candles: Partial<Record<ChartInterval, Candle[]>>;
  live: Partial<Record<ChartInterval, boolean>>;
  markets: Partial<Record<ChartInterval, MarketResolution>>;
} {
  const candles: Partial<Record<ChartInterval, Candle[]>> = {};
  const live: Partial<Record<ChartInterval, boolean>> = {};
  const markets: Partial<Record<ChartInterval, MarketResolution>> = {};
  for (const result of results) {
    candles[result.interval] = result.candles;
    live[result.interval] = result.live;
    if (result.resolution) markets[result.interval] = result.resolution;
  }
  return { candles, live, markets };
}

export function useCoinAnalysis(symbol: string): CoinAnalysisState {
  // Initialize from the session store so the chosen chart interval survives
  // the coin page unmounting/remounting; explicit changes are remembered.
  const [selectedInterval, setSelectedIntervalState] = useState<ChartInterval>(() => getSelectedInterval());
  const setSelectedInterval = useCallback((interval: ChartInterval): void => {
    rememberSelectedInterval(interval);
    setSelectedIntervalState(interval);
  }, []);
  const [detail, setDetail] = useState<CoinDetailSnapshot>(() => emptyDetail(symbol));
  const [candleMap, setCandleMap] = useState<Partial<Record<ChartInterval, Candle[]>>>({});
  const [liveMap, setLiveMap] = useState<Partial<Record<ChartInterval, boolean>>>({});
  const [marketMap, setMarketMap] = useState<Partial<Record<ChartInterval, MarketResolution>>>({});
  const [serverOffset, setServerOffset] = useState(0);
  const [clockNow, setClockNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [streamStatus, setStreamStatus] = useState<KlineStreamState | 'idle'>('idle');
  const [streamMessage, setStreamMessage] = useState('Waiting for a resolved market.');

  const appendWarning = (message: string): void => {
    setError((previous) => (previous?.includes(message) ? previous : [previous, message].filter(Boolean).join(' ')));
  };

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);
    setCandleMap({});
    setLiveMap({});
    setMarketMap({});
    setDetail(emptyDetail(symbol));
    setStreamStatus('idle');
    setStreamMessage('Resolving a live market.');

    const initialize = async (): Promise<void> => {
      try {
        const [detailResult, timeResult] = await Promise.allSettled([getCoinDetail(symbol), getBinanceServerOffset()]);
        if (!active) return;

        const nextDetail = detailResult.status === 'fulfilled' ? detailResult.value : emptyDetail(symbol);
        const resolvedOffset = timeResult.status === 'fulfilled' ? timeResult.value : 0;
        setDetail(nextDetail);
        setServerOffset(resolvedOffset);

        const coreResults = await loadIntervals(symbol, CORE_INTERVALS, Date.now() + resolvedOffset);
        if (!active) return;
        const core = intervalMaps(coreResults);
        setCandleMap(core.candles);
        setLiveMap(core.live);
        setMarketMap(core.markets);

        const hasUsableCandles = coreResults.some((result) => result.candles.length > 0);
        setLoading(false);
        if (detailResult.status === 'rejected') {
          appendWarning('Some live coin information was unavailable. Missing values remain clearly marked.');
        }
        if (timeResult.status === 'rejected') {
          appendWarning('Exchange-time synchronization was unavailable; candle timing temporarily uses the device clock.');
        }
        if (!hasUsableCandles) {
          appendWarning('Live candle history could not be resolved for this symbol. Pilum leaves the chart unavailable instead of fabricating market data.');
        }

        // Lower-priority bridge intervals load after the primary analysis is usable.
        const backgroundResults = await loadIntervals(symbol, BACKGROUND_INTERVALS, Date.now() + resolvedOffset);
        if (!active) return;
        const background = intervalMaps(backgroundResults);
        setCandleMap((previous) => ({ ...previous, ...background.candles }));
        setLiveMap((previous) => ({ ...previous, ...background.live }));
        setMarketMap((previous) => ({ ...previous, ...background.markets }));
      } catch (reason) {
        if (!active) return;
        appendWarning(`Coin analysis initialization failed safely: ${reason instanceof Error ? reason.message : 'unknown error'}.`);
      } finally {
        if (active) setLoading(false);
      }
    };

    void initialize();
    return () => {
      active = false;
    };
  }, [symbol]);

  useEffect(() => {
    let active = true;
    const refresh = async (): Promise<void> => {
      try {
        const refreshed = await getCoinDetail(symbol);
        if (!active) return;
        setDetail((previous) => ({
          ...refreshed,
          price: previous.price > 0 && refreshed.price <= 0 ? previous.price : refreshed.price,
        }));
      } catch (reason) {
        if (!active) return;
        appendWarning(`Coin information refresh failed; the last resolved snapshot remains visible. ${reason instanceof Error ? reason.message : ''}`.trim());
      }
    };
    const timer = window.setInterval(() => void refresh(), 60_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [symbol]);

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(candleMap, selectedInterval)) return;
    let active = true;
    const loadSelected = async (): Promise<void> => {
      try {
        const result = await fetchCandlesWithFallback(symbol, selectedInterval, Date.now() + serverOffset);
        if (!active) return;
        setCandleMap((previous) => ({ ...previous, [selectedInterval]: result.candles }));
        setLiveMap((previous) => ({ ...previous, [selectedInterval]: result.live }));
        if (result.resolution) setMarketMap((previous) => ({ ...previous, [selectedInterval]: result.resolution }));
        if (!result.live) appendWarning(`Verified ${selectedInterval} candle history is unavailable for this symbol.`);
      } catch (reason) {
        if (!active) return;
        setCandleMap((previous) => ({ ...previous, [selectedInterval]: [] }));
        setLiveMap((previous) => ({ ...previous, [selectedInterval]: false }));
        appendWarning(`Unable to load ${selectedInterval} candle history: ${reason instanceof Error ? reason.message : 'unknown error'}.`);
      }
    };
    void loadSelected();
    return () => {
      active = false;
    };
  }, [symbol, selectedInterval, candleMap, serverOffset]);

  useEffect(() => {
    const resolution = marketMap[selectedInterval];
    if (!resolution) {
      setStreamStatus('idle');
      setStreamMessage(`No resolved ${selectedInterval} market is available for live streaming.`);
      return undefined;
    }
    const onUpdate = ({ candle, eventTime }: { candle: Candle; eventTime: number }): void => {
      setCandleMap((previous) => {
        const existing = previous[selectedInterval] ?? [];
        const next = [...existing];
        const last = next.at(-1);
        if (last?.openTime === candle.openTime) next[next.length - 1] = candle;
        else if (!last || candle.openTime > last.openTime) next.push(candle);
        return { ...previous, [selectedInterval]: next.slice(-500) };
      });
      setDetail((previous) => ({ ...previous, price: candle.close, updatedAt: eventTime }));
    };
    const options = {
      onStatus: (status: { state: KlineStreamState; message: string }): void => {
        setStreamStatus(status.state);
        setStreamMessage(status.message);
        if (status.state === 'unavailable') appendWarning(status.message);
      },
    };
    if (resolution.exchange === 'bybit') {
      return subscribeBybitKline(symbol, selectedInterval, resolution.marketKind, onUpdate, options);
    }
    if (resolution.exchange === 'okx') {
      return subscribeOkxKline(symbol, selectedInterval, resolution.marketKind, onUpdate, options);
    }
    return subscribeBinanceKline(symbol, selectedInterval, resolution.marketKind, onUpdate, options);
  }, [symbol, selectedInterval, marketMap]);

  const candles = candleMap[selectedInterval] ?? [];
  const timing = useMemo(
    () => getCandleTiming(selectedInterval, clockNow, serverOffset),
    [selectedInterval, clockNow, serverOffset],
  );

  const assessments = useMemo(
    () =>
      CHART_INTERVALS.filter((item) => item.value !== '1m').map((item) => {
        const intervalCandles = candleMap[item.value] ?? [];
        const intervalTiming = getCandleTiming(item.value, clockNow, serverOffset);
        return assessTimeframe(item.value, intervalCandles, intervalTiming.maturity);
      }),
    [candleMap, clockNow, serverOffset],
  );

  const zones = useMemo(
    () => detectStructuralZones(candles, detail.price || candles.at(-1)?.close || 0, 8),
    [candles, detail.price],
  );
  const breakthrough = useMemo(() => assessHistoricalBreakthrough(candles), [candles]);
  const currentRsi = useMemo(
    () => relativeStrengthIndex(candles.map((candle) => candle.close), 14).at(-1) ?? Number.NaN,
    [candles],
  );
  const candleStudy = useMemo(() => studyCurrentCandle(candles, timing.maturity), [candles, timing.maturity]);

  const sourceMode = useMemo<CoinAnalysisState['sourceMode']>(() => {
    const liveStates = Object.values(liveMap);
    if (liveStates.length > 0 && liveStates.every(Boolean)) return 'live';
    if (liveStates.some(Boolean)) return 'mixed';
    return 'unavailable';
  }, [liveMap]);

  const narrative = useMemo(() => {
    const selected = assessments.find((assessment) => assessment.interval === selectedInterval);
    const oneHour = assessments.find((assessment) => assessment.interval === '1h');
    const fourHour = assessments.find((assessment) => assessment.interval === '4h');
    const oneDay = assessments.find((assessment) => assessment.interval === '1d');
    const oneWeek = assessments.find((assessment) => assessment.interval === '1w');
    const latest = candles.at(-1);
    const priorClosed = candles.slice(0, latest?.closed ? undefined : -1).at(-1);
    const shortMove = latest && priorClosed ? percentChange(latest.close, priorClosed.close) : 0;

    return [
      `${selectedInterval} structure is ${selected?.state.replace('-', ' ') ?? 'unresolved'}; the active candle is ${timing.phase} at ${timing.maturity.toFixed(0)}% maturity with ${Math.max(0, Math.ceil(timing.remainingMs / 1_000))} seconds remaining.`,
      `${oneHour ? `1-hour pressure is ${oneHour.state.replace('-', ' ')}` : '1-hour pressure is unavailable'} and ${fourHour ? `4-hour pressure is ${fourHour.state.replace('-', ' ')}` : '4-hour pressure is unavailable'}. They adjust context and risk; they do not command the selected horizon.`,
      `${oneDay ? `Daily pressure: ${oneDay.state.replace('-', ' ')}` : 'Daily pressure unavailable'}; ${oneWeek ? `weekly pressure: ${oneWeek.state.replace('-', ' ')}` : 'weekly pressure unavailable'}. These are wider reflectors rather than short-term entry signals.`,
      Number.isFinite(currentRsi)
        ? `RSI is ${currentRsi.toFixed(1)} and remains supporting evidence only. The active price is ${shortMove >= 0 ? 'above' : 'below'} the latest completed ${selectedInterval} close by ${Math.abs(shortMove).toFixed(2)}%.`
        : `RSI and active-price comparison are unavailable until enough verified ${selectedInterval} candles load.`,
      breakthrough.explanation,
      detail.observedDriverExplanation,
    ];
  }, [assessments, selectedInterval, timing, candles, currentRsi, breakthrough.explanation, detail.observedDriverExplanation]);

  return {
    loading,
    sourceMode,
    streamStatus,
    streamMessage,
    detail,
    selectedInterval,
    setSelectedInterval,
    candles,
    assessments,
    zones,
    breakthrough,
    currentRsi,
    candleStudy,
    candleTiming: timing,
    narrative,
    ...(error ? { error } : {}),
  };
}
